import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ClubPointRow } from '@smile/shared';
import { ApiError, api } from '../api/client';
import { CZK } from '../lib/format';
import { longDt } from '../lib/filters';
import { can } from '../lib/perms';

/**
 * Klub → body — the legacy club_points page inside the member's detail: the
 * ledger newest first, a form for a manual line (bonus, or a correction with
 * a minus), and delete on manual lines only. Automatic lines carry the
 * booking they came from and are corrected with a negative line, never
 * removed, so the member's history stays honest.
 */
function errText(err: unknown): string | null {
  if (err instanceof ApiError) return err.serverMessage ?? (err.code === 'forbidden' ? 'Na tuhle akci nemáte oprávnění.' : err.message);
  return err instanceof Error ? err.message : null;
}

export function ClubPointsLedger({ clubUserId, rows, capped }: { clubUserId: number; rows: ClubPointRow[]; capped: boolean }) {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 10 * 60_000 });
  const canWrite = can(me.data, '/clients/*', 'write');
  const [parking, setParking] = useState('');
  const [extras, setExtras] = useState('');
  const [note, setNote] = useState('');
  const [rez, setRez] = useState('');
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ['client', clubUserId] });
    await qc.invalidateQueries({ queryKey: ['clients'] });
  };
  const add = useMutation({
    mutationFn: () =>
      api.addClubPoints(clubUserId, {
        pointsParking: Number(parking) || 0,
        pointsExtras: Number(extras) || 0,
        note: note.trim(),
        reservationId: rez.trim() ? Number(rez) : null,
      }),
    onSuccess: async () => {
      setParking('');
      setExtras('');
      setNote('');
      setRez('');
      await refresh();
    },
  });
  const del = useMutation({
    mutationFn: (pointId: number) => api.deleteClubPoints(clubUserId, pointId),
    onSuccess: refresh,
  });
  const p = Number(parking) || 0;
  const x = Number(extras) || 0;
  const ok = (p !== 0 || x !== 0) && Number.isInteger(p) && Number.isInteger(x) && (!rez.trim() || /^\d+$/.test(rez.trim()));
  const message = errText(add.error) ?? errText(del.error);

  return (
    <div className="klienthist body">
      <h3>
        Pohyby bodů <span className="cnt">{rows.length}{capped ? '+' : ''}</span>
      </h3>
      {rows.length === 0 ? (
        <div className="state">Zatím žádný pohyb.</div>
      ) : (
        <table className="mini ledger">
          <thead>
            <tr>
              <th>Kdy</th>
              <th className="num">Parkování</th>
              <th className="num">Služby</th>
              <th>Poznámka</th>
              <th>Rezervace</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="was">{longDt(r.added)}</td>
                <td className={`num ${r.pointsParking < 0 ? 'neg' : ''}`}>{r.pointsParking ? CZK.format(r.pointsParking) : ''}</td>
                <td className={`num ${r.pointsExtras < 0 ? 'neg' : ''}`}>{r.pointsExtras ? CZK.format(r.pointsExtras) : ''}</td>
                <td>
                  {r.note}
                  {r.sourceClubUserId ? <span className="was"> · za člena #{r.sourceClubUserId}</span> : null}
                </td>
                <td>{r.reservationId ? <Link to={`/rezervace/${r.reservationId}`}>{r.reservationId}</Link> : <span className="was">ručně</span>}</td>
                <td className="act">
                  {canWrite && !r.reservationId && (
                    <button type="button" className="btn sm" disabled={del.isPending} onClick={() => { if (window.confirm(`Smazat řádek ${r.note ? `„${r.note}“ ` : ''}(${r.pointsParking + r.pointsExtras} bodů)? Nejde to vrátit.`)) del.mutate(r.id); }} title="Smazat tento ruční řádek">
                      ×
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {capped && <div className="basis">Zobrazeno posledních {rows.length}; starší pohyby zůstávají v databázi.</div>}
      {canWrite && (
        <form
          className="editform"
          style={{ marginTop: 10 }}
          onSubmit={(e) => {
            e.preventDefault();
            if (ok && !add.isPending) add.mutate();
          }}
        >
          <div className="efgrid">
            <label className="ef">
              Body za parkování
              <input type="number" step={1} value={parking} onChange={(e) => setParking(e.target.value)} placeholder="např. 200 nebo -89" />
            </label>
            <label className="ef">
              Body za služby
              <input type="number" step={1} value={extras} onChange={(e) => setExtras(e.target.value)} />
            </label>
            <label className="ef">
              Rezervace (nepovinné)
              <input inputMode="numeric" value={rez} onChange={(e) => setRez(e.target.value)} placeholder="číslo" />
            </label>
            <label className="ef">
              Poznámka
              <input value={note} maxLength={255} onChange={(e) => setNote(e.target.value)} placeholder="proč" />
            </label>
          </div>
          <div className="efactions">
            <button type="submit" className="btn primary" disabled={!ok || add.isPending}>
              {add.isPending ? 'Zapisuji…' : 'Připsat body'}
            </button>
            <span className="was">Záporné číslo body odebírá.</span>
            {message && (
              <span className="arrive-error" role="alert">
                {message}
              </span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
